import './styles.css'

import { format } from 'date-fns'
import FusionCharts from 'fusioncharts'
import charts from 'fusioncharts/fusioncharts.charts'
import { useEffect, useMemo, useState } from 'react'
import ReactFusioncharts from 'react-fusioncharts'
import NumberFormat from 'react-number-format'

import { useBTC } from '../../hooks/use-btc/use-btc'
import { useDebounce } from '../../hooks/use-debounce'
import { formatCryptoAmount, formatFiatAmount } from '../../lib/helpers'
import { Card } from '../card'
import { DatePicker } from '../date-picker'
import { BalanceChartSkeleton } from './balance-chart.skeleton'

const dayInSeconds = 86400000

export interface BalanceChartProps {
    bank?: { date: Date; value: number }[] | undefined
    crypto?: { date: Date; value: number }[] | undefined
    loading: boolean
    date: any
    setDate: any
    totalBalance: number
}

const getDays = (startDate: Date | null, endDate: Date | null) => {
    const start = !startDate ? new Date(0) : new Date(startDate.getTime())
    const end = !endDate ? new Date() : new Date(endDate.getTime())

    const days = []
    let current = start
    while (current <= end) {
        days.push(current)
        current = new Date(current.getTime() + dayInSeconds)
    }
    return days
}

export function BalanceChart({
    bank = [],
    crypto = [],
    loading,
    date,
    setDate,
    totalBalance = 0,
}: BalanceChartProps) {
    const BTCValue = useBTC()

    const [entities, setEntities] = useState({
        bank: true,
        crypto: true,
        both: false,
    })

    const days = useMemo(
        () =>
            getDays(date[0], date[1]).map(d => ({
                label: format(d, 'dd/MM'),
            })),
        [date],
    )
    const dataSource = useMemo(
        () => ({
            chart: {
                yaxisname: 'USD',
                showhovereffect: '1',
                drawcrossline: '1',
                plotToolText:
                    '<div class="tooltip-row"><p class="seriesname $seriesname">$seriesname</p> $$dataValue</div>',
                formatNumberScale: '1',
                theme: 'gammel',
                showBorder: '0',
                bgColor: '#fff',
                bgAlpha: '0',
                canvasBorderThickness: '0',
                showAlternateHGridColor: '0',
                showLegend: '0',
                showShadow: '0',
                crossLineAnimation: '1',

                anchorHoverEffect: '1',
                anchorBgHoverColor: 'red',
            },
            categories: [
                {
                    category: days,
                },
            ],
            dataset: [
                {
                    color: '#9B51E0',
                    seriesname: 'Bank',
                    data: entities.bank
                        ? bank.map(bb => ({
                              value: bb.value,
                              displayValue: bank.length < 30 ? undefined : ' ',
                          }))
                        : [],
                },
                {
                    color: '#219653',
                    seriesname: 'Crypto',
                    data: entities.crypto
                        ? crypto.map(cb => ({
                              value: cb.value,
                              displayValue: bank.length < 30 ? undefined : ' ',
                          }))
                        : [],
                },
                {
                    color: '#3282f6',
                    seriesname: 'All',
                    data: entities.both
                        ? bank.map((cb, i) => ({
                              value:
                                  cb.value +
                                  (crypto.length - 1 < i ? 0 : crypto[i].value),
                              displayValue: ' ',
                          }))
                        : [],
                },
            ],
        }),
        [days, bank, crypto, entities],
    )

    useEffect(() => {
        charts(FusionCharts)
    }, [])

    // Making chart responsive
    const [dimensions, setDimensions] = useState({
        height: window.innerHeight,
        width: window.innerWidth,
    })

    const debouncedDimensions = useDebounce({ value: dimensions, delay: 300 })

    useEffect(() => {
        const updateResize = () => {
            setDimensions({
                height: window.innerHeight,
                width: window.innerWidth,
            })
        }
        window.addEventListener('resize', updateResize)

        return () => {
            window.removeEventListener('resize', updateResize)
        }
    })

    const [hideGraph, setHideGraph] = useState(false)

    useEffect(() => {
        setHideGraph(true)
        setTimeout(() => setHideGraph(false))
    }, [debouncedDimensions])

    return (
        <Card className='w-full h-full'>
            <div className='flex flex-col justify-between px-8 md:flex-row'>
                <div>
                    <p className='flex flex-row items-center gap-2 m-0 text-3xl font-bold text-black'>
                        <NumberFormat
                            value={formatFiatAmount(totalBalance)}
                            className='block'
                            displayType={'text'}
                            thousandSeparator={true}
                            prefix={'$'}
                            //@ts-ignore
                            renderText={(value, props) => (
                                <div {...props}>{value}</div>
                            )}
                        />
                        <span
                            className='text-sm font-thin text-gray'
                            data-test-id='chart-total-balance'>
                            {formatCryptoAmount(totalBalance / BTCValue)} BTC
                        </span>
                    </p>
                    <p className='text-sm text-gray3'>Net worth today</p>
                </div>
                <DatePicker value={date} onChange={setDate} />
            </div>

            <div className='flex flex-col-reverse md:flex-row-reverse'>
                <div className='flex flex-row w-full gap-2 pl-8 md:w-4/12 md:flex-col'>
                    <p
                        className={`cursor-pointer text-sm flex flex-row items-center my-1 ${
                            entities.bank ? 'text-black' : 'text-gray'
                        }`}
                        onClick={() =>
                            setEntities({ ...entities, bank: !entities.bank })
                        }>
                        <div
                            className='mr-2 rounded-full bg-chartViolet'
                            style={{ width: 6, height: 6 }}></div>
                        Bank accounts
                    </p>
                    <p
                        className={`cursor-pointer text-sm  flex flex-row items-center my-1 ${
                            entities.crypto ? 'text-black' : 'text-gray'
                        }`}
                        onClick={() =>
                            setEntities({
                                ...entities,
                                crypto: !entities.crypto,
                            })
                        }>
                        <div
                            className='mr-2 rounded-full bg-chartGreen'
                            style={{ width: 6, height: 6 }}></div>
                        Crypto accounts
                    </p>
                    <p
                        className={`cursor-pointer text-sm  flex flex-row items-center my-1 ${
                            entities.both ? 'text-black' : 'text-gray'
                        }`}
                        onClick={() =>
                            setEntities({ ...entities, both: !entities.both })
                        }>
                        <div
                            className='mr-2 rounded-full bg-blue'
                            style={{ width: 6, height: 6 }}></div>
                        All accounts
                    </p>
                </div>
                <div className='w-11/12' data-test-id='balance-chart'>
                    {loading || hideGraph ? (
                        <BalanceChartSkeleton />
                    ) : (
                        <ReactFusioncharts
                            type='msline'
                            width='100%'
                            height='400'
                            dataFormat='JSON'
                            loading={loading}
                            dataSource={dataSource}
                        />
                    )}
                </div>
            </div>
        </Card>
    )
}
